import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { http, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { ENQUIRY_STATUSES } from "@/lib/constants";
import PatientForm from "@/components/PatientForm";
import { downloadCSV } from "@/lib/csv";

export default function Patients() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [editing, setEditing] = useState(null);

  const { data: patients = [], isLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await http.get("/patients")).data,
  });

  const { data: sourceData } = useQuery({
    queryKey: ["sources"],
    queryFn: async () => (await http.get("/sources")).data,
  });
  const sources = useMemo(() => [...(sourceData?.default || []), ...(sourceData?.custom || [])], [sourceData]);

  const del = useMutation({
    mutationFn: async (id) => http.delete(`/patients/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["patients"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const filtered = useMemo(() => {
    return patients.filter((p) => {
      if (q && !(`${p.patient_id} ${p.source_details || ""}`).toLowerCase().includes(q.toLowerCase())) return false;
      if (source !== "all" && p.acquisition_source !== source) return false;
      if (status !== "all" && p.enquiry_status !== status) return false;
      if (dateFrom && p.enquiry_date < dateFrom) return false;
      return true;
    });
  }, [patients, q, source, status, dateFrom]);

  const exportCsv = () => {
    downloadCSV("patientflow-patients.csv",
      ["patient_id","enquiry_date","consultation_date","acquisition_source","source_details","enquiry_status","treatment_status","sessions_purchased","revenue","notes"],
      filtered);
    toast.success("CSV downloaded");
  };

  return (
    <div className="space-y-5" data-testid="patients-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">Patients</h1>
          <p className="text-sm text-zinc-500 mt-1">Search, filter and manage your acquisition records.</p>
        </div>
        <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="export-csv-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card className="p-4 border-zinc-200">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input placeholder="Search by ID or referrer" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="patients-search" />
          </div>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {ENQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="From date" data-testid="filter-date-from" />
        </div>
      </Card>

      <Card className="border-zinc-200 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-zinc-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            No patient records yet. Click <span className="font-medium text-zinc-800">&quot;+ Add New Patient&quot;</span> to create your first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Patient</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Treatment</th>
                  <th className="text-left px-4 py-3">Revenue</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p._id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`patient-row-${p._id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-900">{p.patient_id}</div>
                      {p.source_details && <div className="text-xs text-zinc-500 mt-0.5">via {p.source_details}</div>}
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{p.enquiry_date}</td>
                    <td className="px-4 py-3 text-zinc-700">{p.acquisition_source}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="border-zinc-200 text-zinc-700 font-normal">{p.enquiry_status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{p.treatment_status}</td>
                    <td className="px-4 py-3 text-zinc-700">{p.revenue ? `₹ ${Number(p.revenue).toLocaleString()}` : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-patient-${p._id}`}>
                          <Pencil className="h-4 w-4 text-zinc-600" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => { if (window.confirm("Delete this record?")) del.mutate(p._id); }} data-testid={`delete-patient-${p._id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && <PatientForm patient={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
